/**
 * SaveSearchButton — opens a modal to save current search filters and email.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Bell, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SaveSearchButton = ({ filters = {}, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(localStorage.getItem('br_unlocked_email') || '');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const label = () => {
    const parts = [];
    if (filters.cities) parts.push(filters.cities);
    if (filters.minbeds) parts.push(`${filters.minbeds}+ bd`);
    if (filters.maxprice) parts.push(`under $${filters.maxprice.toLocaleString()}`);
    return parts.join(' · ') || 'Custom search';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return toast.error('Email required');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/saved-searches`, {
        email,
        name: name || label(),
        cities: filters.cities || null,
        minprice: filters.minprice || null,
        maxprice: filters.maxprice || null,
        minbeds: filters.minbeds || null,
        maxbeds: filters.maxbeds || null,
        minbaths: filters.minbaths || null,
        type: filters.type || null,
      });
      toast.success("You'll get an email the moment new listings match.");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your search');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className={`border-[#BCA480] text-[#BCA480] hover:bg-[#BCA480] hover:text-white ${className}`}
        data-testid="save-search-btn"
      >
        <Bell className="w-4 h-4 mr-2" />
        Save Search & Get Alerts
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="save-search-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#BCA480]" />
              Get first access to new listings
            </DialogTitle>
            <DialogDescription>
              We'll email you the moment a new property matches: <strong>{label()}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="save-search-email"
              />
            </div>
            <div>
              <Label>Name for this search (optional)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Olive Branch 4bd"
                data-testid="save-search-name"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#BCA480] hover:bg-[#a89470] text-white" disabled={submitting} data-testid="save-search-submit">
                {submitting ? 'Saving…' : 'Start Alerts'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SaveSearchButton;
